import React from 'react';
import { Row, TableBodyPropGetter, TableBodyProps, Cell, TableCommonProps } from 'react-table';
import { generatePath, Link } from 'react-router-dom';

import { SvyTooltip } from '@/components/savory/SvyTooltip';

import { TableRow, TableCell } from '../Table.type';

type NormalTableProps = {
    page: Row<TableRow>[];
    prepareRow: (row: Row<TableRow>) => void;
    getTableBodyProps: (propGetter?: TableBodyPropGetter<TableRow> | undefined) => TableBodyProps;
    isCellClickable: (cell: Cell<TableRow, TableCell>) => boolean;
    handleOnCellClick: (cell: Cell<TableRow, TableCell>, event?: React.MouseEvent<HTMLTableCellElement>) => void;
    customRowProps?: (row: TableRow) => TableCommonProps;
    /** Path pattern for generating row links (enables right-click "Open in new tab") */
    onRowClickPath?: string;
};

const NON_LINKABLE_CELL_TYPES = ['action', 'switch', 'dropdownText', 'selection'];

export const NormalTable: React.FunctionComponent<React.PropsWithChildren<NormalTableProps>> = ({
    page,
    prepareRow,
    getTableBodyProps,
    isCellClickable,
    handleOnCellClick,
    customRowProps,
    onRowClickPath,
}) => {
    const getRowLinkPath = (row: Row<TableRow>): string | null => {
        if (!onRowClickPath || !row.original.linkTo?.value) return null;
        return generatePath(onRowClickPath, row.original.linkTo.value as Record<string, string>);
    };

    const isCellLinkable = (cell: Cell<TableRow, TableCell>): boolean => {
        const cellType = cell.value?.type || cell.column.id;
        return !NON_LINKABLE_CELL_TYPES.includes(cellType);
    };

    const renderCellContent = (
        cell: Cell<TableRow, TableCell>,
        renderedCell: React.ReactNode,
        rowLinkPath: string | null,
    ): React.ReactNode => {
        const cellValue = cell?.value as TableCell & { tooltip?: string };
        const content = cellValue?.tooltip ? (
            <SvyTooltip tooltipContent={cellValue.tooltip}>
                <div>{renderedCell}</div>
            </SvyTooltip>
        ) : (
            renderedCell
        );

        if (rowLinkPath && isCellLinkable(cell)) {
            return (
                <Link
                    to={rowLinkPath}
                    onClick={(e) => e.preventDefault()}
                    style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}
                >
                    {content}
                </Link>
            );
        }

        return content;
    };

    return (
        <tbody {...getTableBodyProps()}>
            {page.map((row) => {
                prepareRow(row);
                const { key: rowKey, ...tableRowProps } = row.getRowProps(
                    customRowProps ? customRowProps(row.original) : {},
                );
                const rowLinkPath = getRowLinkPath(row);

                return (
                    <tr key={rowKey} {...tableRowProps}>
                        {row.cells.map((cell) => {
                            const { key: cellKey, ...cellProps } = cell.getCellProps();
                            const renderedCell = cell.render('Cell');

                            return (
                                <td
                                    key={cellKey}
                                    {...cellProps}
                                    style={{
                                        minWidth: cell.column.minWidth,
                                        width: cell.column.width,
                                        maxWidth: cell.column.maxWidth,
                                    }}
                                    className={isCellClickable(cell) ? 'clickable' : ''}
                                    onClick={(event) => handleOnCellClick(cell, event)}
                                >
                                    {renderCellContent(cell, renderedCell, rowLinkPath)}
                                </td>
                            );
                        })}
                    </tr>
                );
            })}
        </tbody>
    );
};
